"use client";

import React, { useCallback, useEffect, useRef } from "react";
import { Controller } from "react-hook-form";
import cn, { clsx } from "clsx";
import styles from "./NewBannerFormLp.module.scss";
import { usePathname } from "next/navigation";
import { useInView } from "react-intersection-observer";
import { useModalDispatch } from "@/context/ModalContext";
import { IModalActions } from "@/types/modal";
import StatsContainerLp from "../StatsContainerLp/StatsContainerLp";
import { ALL_COUNTRIES } from "@/context/countries";
import useOnScreen from "@/helpers/Onscreen";
import StatsContainerFaqLp from "../StatsContainerFaqLp/StatsContainerFaqLp";
import CountryDropdownGlobalWhite from "@/components/atoms/FormComponents/CustomDropDownWhite/CustomDropdownGlobalWhite";
import Checkbox from "@/components/atoms/FormComponents/Checkbox";
import DelayCommonModal from "@/components/organisms/modal/ConnectModal/ConnectModalWrapper";
import { disabledButtonPaths } from "@/components/molecules/Forms/LandingPageConnectModalForm/utils";
import { StorageService } from "@/helpers/storage.service";
import { clearSubmitBusyDom } from "@/helpers/paintFirst";
import InpSafeForm from "@/components/atoms/FormComponents/InpSafeForm";
import { useProgramDetailsOpenRfiController } from "@/components/organisms/NewBannerForm/controller/useProgramDetailsOpenRfiController";

interface BannerImageTempleteType {
  ShortDescLabel1?: string;
  ShortDescLabel2?: string;
  ShortDescLabel3?: string;
  ShortDescSubLabel1?: string;
  ShortDescSubLabel2?: string;
  ShortDescSubLabel3?: string;
  SeatsFilled?: string;
}

export default function ProgramDetailsOpenRfi(props: {
  ProgramId?: any;
  associatedPrograms: Array<ProgramDetails>;
  isMobile?: boolean;
  setLeadFailed?: (x: boolean) => void;
  setApplicationRedirectLoaderDisabled: (value: boolean) => void;
  isFaq?: boolean;
  BannerImageTemplete?: BannerImageTempleteType;
  setLoadingCount?: any;
  counterInterval?: any;
  setRedirectEmail: (email: string) => void;
}) {
  const path = usePathname();
  const { isMobile, setApplicationRedirectLoaderDisabled, BannerImageTemplete, isFaq } = props;

  const modalDispatch = useModalDispatch();
  const isSessionModalActive = StorageService.get("timeConnectModal");
  const SeatFilledCount = Number(BannerImageTemplete?.SeatsFilled);

  const ref = useRef(null);
  const { ref: formRef, inView } = useInView();
  const setRefs = useCallback(
    (node) => {
      ref.current = node;
      formRef(node);
    },
    [formRef],
  );

  const onScreen = useOnScreen(ref);

  // Hide the floating "enroll now" CTA while this form is on-screen.
  useEffect(() => {
    const enrollNowRef = document.getElementById("enroll-now-cta");
    if (enrollNowRef) {
      enrollNowRef.style.display = onScreen ? "none" : "block";
    }
  }, [onScreen]);

  // Suppress the connect modal from popping while this form is in view,
  // unless the user already dismissed it this session.
  useEffect(() => {
    if (!isSessionModalActive) {
      modalDispatch({
        type: IModalActions.UpdateModal,
        data: {
          isModalForceClose: inView,
        },
      });
    }
  }, [inView, isSessionModalActive, modalDispatch]);

  const {
    control,
    errors,
    isSubmitting,
    isOTPLoading,
    isFormValid,
    message,
    handleFieldChange,
    handleFieldBlur,
    updateCountryCode,
    handleFormButtonClick,
    handlePreDegree,
  } = useProgramDetailsOpenRfiController({
    ProgramId: props.ProgramId,
    associatedPrograms: props.associatedPrograms,
    isLandingPage: false,
    isLambdaAPI: true,
    programsOptions: [],
    defaultCountryCode: "+91",
    countryList: ALL_COUNTRIES,
    setApplicationRedirectLoaderDisabled,
    setRedirectEmail: props.setRedirectEmail,
    resolveProgramIdFromAssociatedPrograms: true,
    webengageLoginIdField: "ProspectId",
  });

  const isLoading = isSubmitting;

  useEffect(() => {
    if (!isLoading && !isOTPLoading) {
      clearSubmitBusyDom();
    }
  }, [isLoading, isOTPLoading]);

  return (
    <InpSafeForm
      formRef={setRefs}
      onSubmit={handleFormButtonClick}
      onFocus={handlePreDegree}
    >
      <div className={styles.form_container}>
        <div className={styles.field_container}>
          <div className="w-full mt-2">
            <Controller
              name="fullName"
              control={control}
              render={({ field, fieldState }) => (
                <>
                  <input
                    type="text"
                    name="name"
                    placeholder="Full Name"
                    className={cn(styles.text_field, "w-full")}
                    value={field.value ?? ""}
                    onChange={(event) => handleFieldChange("fullName", event.target.value)}
                    onBlur={() => {
                      field.onBlur();
                      handleFieldBlur("fullName", "", fieldState.error?.message ?? errors.fullName);
                    }}
                    id="fullName"
                  />
                  <p className={cn(styles.error)}>{fieldState.error?.message ?? errors.fullName}</p>
                </>
              )}
            />
          </div>

          <div className="lg:mt-3">
            <div className={cn(styles.select_container)}>
              <Controller
                name="countryCode"
                control={control}
                render={({ field }) => (
                  <CountryDropdownGlobalWhite
                    onChangeFunc={(value: string) => handleFieldChange("countryCode", value)}
                    countries={ALL_COUNTRIES}
                    countryCode={field.value}
                    updateCountryCode={updateCountryCode}
                    additionalStyle={cn(styles.text_field, "lining-nums h-full")}
                    isDark={false}
                    disabled={isLoading || isOTPLoading || disabledButtonPaths.includes(path)}
                  />
                )}
              />
              <Controller
                name="phoneNumber"
                control={control}
                render={({ field, fieldState }) => (
                  <input
                    type="text"
                    name="mobile"
                    placeholder="Mobile No."
                    className={cn(styles.text_field, "w-full lining-nums")}
                    value={field.value ?? ""}
                    id="phoneNumber"
                    onChange={(event) => handleFieldChange("phoneNumber", event.target.value)}
                    onBlur={() => {
                      field.onBlur();
                      handleFieldBlur("phoneNumber", "", fieldState.error?.message ?? errors.phoneNumber);
                    }}
                  />
                )}
              />
            </div>
            <p className={cn(styles.error)}>{errors.countryCode || errors.phoneNumber}</p>
          </div>

          <div>
            <Controller
              name="emailId"
              control={control}
              render={({ field, fieldState }) => (
                <>
                  <input
                    type="text"
                    name="email"
                    id="email"
                    placeholder="Email Address"
                    value={field.value ?? ""}
                    className={cn(styles.text_field, "w-full mt-3")}
                    onChange={(event) => handleFieldChange("emailId", event.target.value)}
                    onBlur={field.onBlur}
                  />
                  <p className={cn(styles.error)}>{fieldState.error?.message ?? errors.emailId}</p>
                </>
              )}
            />
          </div>

          <div className={cn(styles.commonMargins, "mt-3")}>
            <div className="flex gap-3 items-start">
              <div className="shrink-0">
                <Controller
                  name="isTermsAccepted"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Checkbox
                      name="consent"
                      value={field.value}
                      onChangeFunc={(value: boolean) => {
                        handleFieldChange("isTermsAccepted", value);
                        DelayCommonModal(
                          handleFieldBlur,
                          "isTermsAccepted",
                          "",
                          fieldState.error?.message ?? errors.isTermsAccepted,
                        );
                      }}
                      disabled={isLoading || isOTPLoading}
                      style={{ padding: "0.4rem" }}
                    />
                  )}
                />
              </div>
              <p className={cn(styles.text)}>
                By entering these details I agree that Amity University Online and its associates can contact me with updates & notifications via Email, SMS, WhatsApp, and Voice call as per its <a href="https://amityonline.com/privacy-policy" target="_blank" rel="noopener noreferrer" style={{ textDecoration: "underline", textDecorationColor: "inherit", cursor: "pointer" }}>Privacy Policy</a>. This consent will override any registration for DNC / NDNC.
              </p>
            </div>
            <p className={cn(styles.termserror)}>{errors.isTermsAccepted}</p>
          </div>

          <div className={cn(styles.btn_container)}>
            <button
              id="submit-button"
              type="button"
              className={clsx(styles.btn, {
                [styles.btnDisabled]: isLoading || isOTPLoading,
              })}
              disabled={isLoading || isOTPLoading}
              onClick={(e) => {
                e.stopPropagation();
                void handleFormButtonClick(e);
              }}
              aria-busy={isLoading || isOTPLoading}
            >
              <span className={styles.btnContent}>
                <span
                  className={cn(
                    styles.btnSpinner,
                    !(isLoading || isOTPLoading) && styles.btnSpinnerHidden,
                  )}
                  aria-hidden={!(isLoading || isOTPLoading)}
                />
                {(isLoading || isOTPLoading) ? "Submitting..." : "Get Free Counselling"}
              </span>
            </button>
          </div>

          {
            !!SeatFilledCount && (
              <div className={cn(styles.filled_seat_container, `${props?.isFaq ? "mt-[1.2rem]" : "mt-3"}`)}>
                <div
                  className={cn(styles.filled_seats)}
                  style={{ "--progress": SeatFilledCount / 100 } as React.CSSProperties}
                >
                  <div className={styles.filled}></div>
                </div>
                <div className={cn(clsx(styles.text), "lining-nums")}>{SeatFilledCount}% Seats filled</div>
              </div>
            )
          }
        </div>
        {isFaq ? <StatsContainerFaqLp BannerImageTemplete={BannerImageTemplete} isStatsDisplay={true} /> :
          <StatsContainerLp BannerImageTemplete={BannerImageTemplete} isStatsDisplay={isMobile} />
        }
        {message && message}
      </div>
    </InpSafeForm>
  );
}
